import React, { useEffect, useMemo, useState } from 'react';
import { t } from '@lingui/macro';
import {
  Button,
  ButtonGroup,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@cardboard-ui/react';
import { FormControl } from '../Form';
import { UpdateImageButton } from '../ImageUploadModal/UpdateImageButton';
import { OverlayProps } from 'components/TaskOverlay';
import { CoverImageInput } from 'apps/TenantApp/screens/Event/components/EventHeader/__generated__/EventHeader_updateEventImageUrl_Mutation.graphql';
import { graphql, useMutation } from 'utils/graphClient';
import { useForm } from 'react-hook-form';
import { SuspendWithSpinner } from '../SuspendWithSpinner';
import { useFragment } from 'utils/graphClient';
import NotFound from '../../apps/TenantApp/screens/NotFound';
import { SpaceShortcutCard } from 'components/SpaceShortcutCard/SpaceShortcutCard';
import {
  CoverImageKind,
  ManageSpaceShortcutModal_createSpaceShortcut_Mutation,
} from './__generated__/ManageSpaceShortcutModal_createSpaceShortcut_Mutation.graphql';
import { ManageSpaceShortcutModal_space$key } from './__generated__/ManageSpaceShortcutModal_space.graphql';
import { ManageSpaceShortcutModal_updateSpaceShortcut_Mutation } from './__generated__/ManageSpaceShortcutModal_updateSpaceShortcut_Mutation.graphql';
import { urlFetcher } from 'components/Editor/LinkCardExtension/LinkCard';
import ImageFallback from 'assets/images/portrait-papers.jpg';

const createSpaceShortcutMutation = graphql`
  mutation ManageSpaceShortcutModal_createSpaceShortcut_Mutation(
    $input: SpacesCreateShortcutMutationInput!
  ) {
    createSpaceShortcut(input: $input) {
      space {
        id
        shortcuts {
          edges {
            node {
              id
              title
              url
              coverImage {
                name
                url
              }
            }
          }
        }
      }
    }
  }
`;

const updateSpaceShortcutMutation = graphql`
  mutation ManageSpaceShortcutModal_updateSpaceShortcut_Mutation(
    $input: SpacesUpdateShortcutMutationInput!
  ) {
    updateSpaceShortcut(input: $input) {
      shortcutUpdated {
        id
        title
        url
        coverImage {
          name
          url
        }
      }
    }
  }
`;

interface ModalProps extends OverlayProps {
  spaceShortcutID?: string;
  defaultTitle?: string;
  defaultUrl?: string;
  defaultImageUrl?: string;
}

interface UnsuspendedManageSpaceShortcutModalProps extends ModalProps {
  spaceId: string;
}

type ImageSelectedType = {
  newImage: CoverImageInput | null;
  newLocalImageURL: string;
  onComplete: () => void;
};

const UnsuspendedManageSpaceShortcutModal = ({
  spaceId,
  spaceShortcutID,
  defaultImageUrl,
  defaultUrl,
  defaultTitle,
  isOpen,
  onClose: onCloseModal,
}: UnsuspendedManageSpaceShortcutModalProps) => {
  /* States */
  const [imageViaFileInput, setImageViaFileInput] =
    useState<ImageSelectedType['newImage']>(null);

  const [imageViaFileInputLocalUri, setImageViaFileInputLocalUri] = useState<
    string | null
  >(null);

  const [imageViaLink, setImageViaLink] = useState<
    ImageSelectedType['newImage']
  >(
    defaultImageUrl
      ? {
          kind: 'URL',
          value: defaultImageUrl,
        }
      : null,
  );

  useEffect(() => {
    // modal isn't destroyed when closing, so we have to ensure the cached values are refreshed
    const valueToSet = defaultImageUrl
      ? {
          kind: 'URL' as CoverImageKind,
          value: defaultImageUrl,
        }
      : null;
    setImageViaLink(valueToSet);
  }, [defaultImageUrl]);

  const fetchUrlContent = async ({ target }: { target: { value: string } }) => {
    const url = target.value;

    try {
      const { image } = await urlFetcher(url);
      if (image) {
        setImageViaLink({
          kind: 'URL',
          value: image,
        });
      } else {
        throw new Error('Invalid request');
      }
    } catch (err) {
      setImageViaLink(null);
    }
  };

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors, isDirty },
    reset,
  } = useForm({ mode: 'all', reValidateMode: 'onBlur' });

  const onClose = () => {
    reset();
    setImageViaFileInput(null);
    setImageViaLink(null);
    setImageViaFileInputLocalUri(null);
    onCloseModal();
  };

  /* Mutations */
  const [createSpaceShortcut, createSpaceShortcutLoading] =
    useMutation<ManageSpaceShortcutModal_createSpaceShortcut_Mutation>(
      createSpaceShortcutMutation,
    );
  const [updateSpaceShortcut, updateSpaceShortcutLoading] =
    useMutation<ManageSpaceShortcutModal_updateSpaceShortcut_Mutation>(
      updateSpaceShortcutMutation,
    );

  /* Variables */
  const isLoading = createSpaceShortcutLoading || updateSpaceShortcutLoading;

  /* Functions */
  const handleOnImageSelected = ({
    newImage,
    newLocalImageURL,
    onComplete,
  }: ImageSelectedType) => {
    setImageViaFileInput(newImage);
    setImageViaFileInputLocalUri(newLocalImageURL);
    clearErrors('image');
    onComplete();

    // image reset coming in
    if (!newImage?.value) {
      setImageViaLink(null);
    }
  };

  const imagePair = useMemo(() => {
    // file upload
    if (imageViaFileInput?.value) {
      return {
        image: imageViaFileInputLocalUri as string,
        api: imageViaFileInput,
      };
    }

    // image fetched via URL, *OR* a URL reference itself
    if (imageViaLink?.value) {
      return {
        image: imageViaLink.value,
        api: imageViaLink,
      };
    }

    // fallback image
    return {
      image: ImageFallback,
      api: {
        kind: 'URL',
        value: ImageFallback,
      },
    };
  }, [
    imageViaFileInput,
    imageViaFileInputLocalUri,
    defaultImageUrl,
    imageViaLink,
  ]);

  const handleOnCompleteTask = ({
    title,
    url,
  }: {
    title: string;
    url: string;
  }) => {
    if (!title || !url) {
      /* Title and url errors are handled from useForm */
      return;
    }

    if (spaceShortcutID) {
      const coverImageChanged = imagePair.api.value !== defaultImageUrl;

      updateSpaceShortcut({
        variables: {
          input: {
            id: spaceShortcutID,
            title: title,
            url: url,
            coverImage: coverImageChanged
              ? (imagePair.api as CoverImageInput)
              : undefined,
          },
        },
        onCompleted: () => {
          onClose();
        },
      });
    } else {
      createSpaceShortcut({
        variables: {
          input: {
            spaceId,
            title: title,
            url: url,
            coverImage: imagePair.api as CoverImageInput,
          },
        },
        onCompleted: () => {
          onClose();
        },
      });
    }
  };

  return (
    <Modal onClose={onClose} size="xl" isOpen={isOpen}>
      <ModalOverlay />
      <ModalContent data-qa="manage-shortcut-modal">
        <ModalHeader>
          {spaceShortcutID ? t`Edit shortcut` : t`Create Shortcut`}
        </ModalHeader>

        <ModalBody>
          <HStack spacing="6" align="top">
            <SpaceShortcutCard
              url={watch('url', defaultUrl)}
              title={watch('title', defaultTitle)}
              imageURL={imagePair.image}
              editing={true}
            />

            <form
              id="manage-shortcut-form"
              onSubmit={handleSubmit(handleOnCompleteTask)}
            >
              <VStack flex={1}>
                <FormControl
                  label={t`Shortcut title`}
                  errorMessage={errors?.title?.message}
                >
                  <Input
                    defaultValue={defaultTitle}
                    placeholder={t`Describe your shortcut`}
                    type="text"
                    {...register('title', {
                      required: t`Title is required`,
                    })}
                  />
                </FormControl>

                <FormControl
                  label={t`Image`}
                  errorMessage={errors?.image?.message}
                >
                  <UpdateImageButton
                    modalHeadingText={t`Set space shortcut image`}
                    cropVariant="portrait"
                    imageURL={defaultImageUrl || ''}
                    onImageSelected={handleOnImageSelected}
                    showDefaultImagesGallery
                    defaultImage={defaultImageUrl}
                  />
                </FormControl>

                <FormControl
                  label={t`Link`}
                  errorMessage={errors?.url?.message}
                >
                  <Input
                    defaultValue={defaultUrl}
                    placeholder={t`Type or paste a link`}
                    type="url"
                    {...register('url', {
                      required: t`Link is required`,
                      validate: (url: string) =>
                        url.startsWith('https://') ||
                        'Please enter a valid link',
                    })}
                    onChange={fetchUrlContent}
                  />
                </FormControl>
              </VStack>
            </form>
          </HStack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup size="sm" spacing="3">
            <Button
              isDisabled={isLoading}
              onClick={onClose}
            >{t`Cancel`}</Button>
            <Button
              type="submit"
              form="manage-shortcut-form"
              colorScheme="blue"
              isLoading={isLoading}
              isDisabled={!isDirty || isLoading}
            >
              {spaceShortcutID ? t`Save` : t`Create`}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

interface ManageSpaceShortcutModalProps extends ModalProps {
  space: ManageSpaceShortcutModal_space$key | null;
}

const manageSpaceShortcutModalFragment = graphql`
  fragment ManageSpaceShortcutModal_space on Space {
    __typename
    id
  }
`;

export const ManageSpaceShortcutModal = ({
  space,
  ...otherProps
}: ManageSpaceShortcutModalProps) => {
  const { id: spaceId } =
    useFragment(manageSpaceShortcutModalFragment, space) || {};

  if (!spaceId) {
    return <NotFound />;
  }

  return (
    <SuspendWithSpinner>
      <UnsuspendedManageSpaceShortcutModal spaceId={spaceId} {...otherProps} />
    </SuspendWithSpinner>
  );
};
